import type { HandlerContext, HandlerUtility, LoggingOptions } from "../types.js";

/**
 * Default logger using console.
 */
function defaultLogger(message: string, data: Record<string, unknown>): void {
    console.log(JSON.stringify({ message, ...data, timestamp: new Date().toISOString() }));
}

/**
 * Convert Headers to plain object.
 */
function headersToObject(headers: Headers): Record<string, string> {
    const result: Record<string, string> = {};
    headers.forEach((value, key) => {
        result[key] = value;
    });
    return result;
}

/**
 * Log with appropriate level.
 */
function logByLevel(
    level: "debug" | "info" | "warn" | "error",
    logger: (message: string, data: Record<string, unknown>) => void,
    message: string,
    data: Record<string, unknown>
): void {
    logger(message, { ...data, level });
}

/**
 * Logging utility for request/response logging.
 *
 * @example
 * ```ts
 * import { compose, withLogging } from "next-route-compose";
 *
 * export const GET = compose(
 *   withLogging({ level: "info" })
 * )(handler);
 *
 * // With custom logger (e.g., Pino, Winston)
 * export const POST = compose(
 *   withLogging({
 *     logger: (msg, data) => pino.info(data, msg)
 *   })
 * )(handler);
 * ```
 */
export function withLogging(options: LoggingOptions = {}): HandlerUtility {
    const {
        level = "info",
        logger = defaultLogger,
        includeBody = false,
        includeResponse = false,
    } = options;

    return (handler) => {
        return async (context: HandlerContext) => {
            const startTime = performance.now();
            const requestId = crypto.randomUUID();

            // Log request
            const requestData: Record<string, unknown> = {
                requestId,
                type: "request",
                method: context.request.method,
                url: context.request.url,
                headers: headersToObject(context.request.headers),
            };

            if (includeBody && context.request.method !== "GET") {
                try {
                    const clonedRequest = context.request.clone();
                    requestData["body"] = await clonedRequest.json();
                } catch {
                    // Body is not JSON or already consumed
                }
            }

            logByLevel(level, logger, "Incoming request", requestData);

            // Add requestId to context
            const enrichedContext: HandlerContext = {
                ...context,
                requestId,
            };

            try {
                const response = await handler(enrichedContext);
                const duration = performance.now() - startTime;

                // Log response
                const responseData: Record<string, unknown> = {
                    requestId,
                    type: "response",
                    status: response.status,
                    duration: `${duration.toFixed(2)}ms`,
                };

                if (includeResponse) {
                    try {
                        const clonedResponse = response.clone();
                        responseData["body"] = await clonedResponse.json();
                    } catch {
                        // Response is not JSON
                    }
                }

                logByLevel(level, logger, "Request completed", responseData);

                // Add request ID to response headers
                const newHeaders = new Headers(response.headers);
                newHeaders.set("X-Request-Id", requestId);

                return new Response(response.body, {
                    status: response.status,
                    statusText: response.statusText,
                    headers: newHeaders,
                });
            } catch (error) {
                const duration = performance.now() - startTime;

                // Log error
                logger("Request failed", {
                    requestId,
                    type: "error",
                    error: error instanceof Error ? error.message : String(error),
                    stack: error instanceof Error ? error.stack : undefined,
                    duration: `${duration.toFixed(2)}ms`,
                });

                throw error;
            }
        };
    };
}

/**
 * Timing utility to measure handler execution time.
 *
 * @example
 * ```ts
 * export const GET = compose(
 *   withTiming()
 * )(handler);
 * // Response will include X-Response-Time header
 * ```
 */
export function withTiming(): HandlerUtility {
    return (handler) => {
        return async (context: HandlerContext) => {
            const startTime = performance.now();
            const response = await handler(context);
            const duration = performance.now() - startTime;

            const newHeaders = new Headers(response.headers);
            newHeaders.set("X-Response-Time", `${duration.toFixed(2)}ms`);

            return new Response(response.body, {
                status: response.status,
                statusText: response.statusText,
                headers: newHeaders,
            });
        };
    };
}
